import { ReactElement } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import moment from "moment";

import { dateLabel } from "../../portfolio-agreement/agreement-dates";

interface ApprovalFieldsProps {
  type?: string | null;
  /** Applicable date, ISO. */
  date: string | null;
  /** The record's date, to tell a change from the original. */
  originalDate: string | null;
  editable: boolean;
  regenerating: boolean;
  onDateChange: (iso: string | null) => void;
}

const sameDay = (a: string | null, b: string | null): boolean => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return dayjs(a).isSame(dayjs(b), "day");
};

/**
 * What the WM can change before approving.
 *
 * Kept apart from the read-only grid because these behave differently: the
 * date regenerates the document below, and neither value is persisted until
 * the request is approved. The unsaved marker exists so an edited value can
 * never be mistaken for one already stored.
 *
 * The date is the only value the WM sets. New fees is required for an appendix
 * but comes from initiation (the upload template's "New Fees" column), so it
 * is shown with the other read-only values above rather than here.
 */
const ApprovalFields: React.FC<ApprovalFieldsProps> = ({
  type,
  date,
  originalDate,
  editable,
  regenerating,
  onDateChange,
}): ReactElement => {
  const name = dateLabel(type);
  // Only the date can change, so only the date can be unsaved.
  const changed = editable && !sameDay(date, originalDate);

  return (
    <div className="date-field">
      <div className="date-field__head">
        <span className="date-field__icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3.5" y="5" width="17" height="16" rx="2.5" />
            <path d="M3.5 10h17M8 3.5v3M16 3.5v3" />
          </svg>
        </span>
        <div>
          <p className="date-field__label">Approval details</p>
          <p className="date-field__hint">
            {editable
              ? "Submitted with the approval. Changing the date regenerates the document below."
              : "Set when the request was decided."}
          </p>
        </div>
        {changed && <span className="date-field__badge">Unsaved change</span>}
      </div>

      <div className="date-field__control">
        <div className="date-field__input">
          <label htmlFor="wm-applicable-date">{name}</label>
          {editable ? (
            <DatePicker
              id="wm-applicable-date"
              value={date ? dayjs(date) : null}
              format="DD MMM YYYY"
              placeholder={`Select ${name.toLowerCase()}`}
              onChange={(next) =>
                onDateChange(next ? dayjs(next).toISOString() : null)
              }
            />
          ) : (
            <p className="date-field__static">
              {date ? moment(date).format("DD MMM YYYY") : "-"}
            </p>
          )}
        </div>

        {regenerating && (
          <span className="date-field__status" role="status">
            <span className="date-field__spinner" aria-hidden="true" />
            Updating preview…
          </span>
        )}
      </div>
    </div>
  );
};

export default ApprovalFields;
